import { useModalContext } from '#pplx/frontend/aether/modals/context/useModalContext.js';
import { isMaybeIncognitoComet, LS_COMET_FULLSCREEN_UPSELL_DISMISSED_KEY, } from '#pplx/frontend/apps/ask/comet-shared/cometUtils.js';
import { useFullScreenCometUpsellVariation } from '#pplx/frontend/apps/ask/generated/assignment-hooks/useFullScreenCometUpsellVariation.js';
import { useIsExistingCometUser } from '#pplx/frontend/apps/ask/hooks/comet-hooks/useIsExistingCometUser.js';
import { useUserCapabilities } from '#pplx/frontend/apps/ask/queries/user-queries/useUserCapabilities.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { useIsUserLoggedIn } from '#pplx/frontend/auth/userHooks.js';
import { SafeLocalStorage } from '#pplx/frontend/browser-storage/localStorage.js';
import { getCookieHasDownloadedComet } from '#pplx/frontend/cookies/metadataCookieManager.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { useIsMobileStyle } from '#pplx/frontend/platform-components/useMobileStyle.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { useEffect } from 'react';
const CometUpsellFullscreenModal = Dynamic(async () => {
    const { CometUpsellFullscreenModal } = await import('#pplx/frontend/apps/ask/upsells/components/CometUpsellFullscreenModal.js');
    return { default: CometUpsellFullscreenModal };
});
/**
 * Auto-opens Comet upsell modal for eligible users based on platform, user group,
 * experiment variation, dismissal state, and existing Comet user status.
 */
export const useCometFullScreenUpsell = () => {
    const { device: { isWindowsOS, isMacOS, isWindowsApp }, } = useWebPlatform();
    const { isGovernmentRequestOrigin } = useUserCapabilities();
    const isMobileStyle = useIsMobileStyle();
    const { openModal } = useModalContext();
    const { isEnterprise } = useIsSubscribed();
    const isLoggedIn = useIsUserLoggedIn();
    const isCometBrowser = isMaybeIncognitoComet();
    const hasDownloadedComet = getCookieHasDownloadedComet();
    const { isExistingCometUser, isLoading: isLoadingExistingCometUser } = useIsExistingCometUser();
    const hasDismissedFullscreenCometUpsell = SafeLocalStorage.getItem(LS_COMET_FULLSCREEN_UPSELL_DISMISSED_KEY) === 'true';
    // The legacy Windows app shows its own full-screen migration modal on the
    // same launch; the two must not stack.
    const isValidPlatformForCometUpsell = (isMacOS || isWindowsOS) &&
        !isWindowsApp &&
        !isCometBrowser &&
        !hasDownloadedComet &&
        !isMobileStyle;
    const isValidUserGroup = !isGovernmentRequestOrigin && !isEnterprise;
    // Note: Dismissal is NOT included here - we want to log exposure even for dismissed users
    // to avoid experiment imbalance. Dismissal is checked later to prevent showing the modal.
    const skipAssignment = !isLoggedIn ||
        isLoadingExistingCometUser ||
        isExistingCometUser ||
        !isValidPlatformForCometUpsell ||
        !isValidUserGroup;
    // These gates define whether the modal could ever show. Skipped users stay
    // control without exposure; dismissed users are still exposed below by design.
    const { variation: fullScreenCometUpsellVariation } = useFullScreenCometUpsellVariation({
        defaultValue: 'control',
        skip: skipAssignment,
    });
    useEffect(() => {
        // The skip conditions ensure we only get a non-control variation for eligible users.
        // If we have a non-control variation, the user passed all eligibility checks.
        // Additionally check dismissal status - respect user's choice to dismiss the modal.
        if (fullScreenCometUpsellVariation !== 'control' && !hasDismissedFullscreenCometUpsell) {
            openModal(CometUpsellFullscreenModal, {
                variation: fullScreenCometUpsellVariation,
            }, { replace: true });
        }
    }, [fullScreenCometUpsellVariation, openModal, hasDismissedFullscreenCometUpsell]);
};
//# sourceMappingURL=useCometFullScreenUpsell.js.map